<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTablesStore } from 'dashboard/stores/tables';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  tableId: { type: Number, required: true },
  columns: { type: Array, required: true },
});

const { t } = useI18n();
const tablesStore = useTablesStore();

const name = ref('');
const description = ref('');
const conditions = ref([{ column: '', operator: 'eq', parameter: '' }]);
const errorMessage = ref('');

const addCondition = () => {
  conditions.value.push({ column: '', operator: 'eq', parameter: '' });
};

// A saved query is what a bot invokes and what a grant is issued against, so
// the editor builds the filter from named parameters rather than letting an
// administrator type a raw expression.
const buildPayload = () => {
  const used = conditions.value.filter(condition => condition.column && condition.parameter);

  return {
    name: name.value.trim(),
    description: description.value.trim(),
    parameters: used.map(condition => ({
      name: condition.parameter,
      type: 'text',
      required: true,
    })),
    filter: Object.fromEntries(
      used.map(condition => [
        condition.column,
        { [condition.operator]: `$${condition.parameter}` },
      ])
    ),
  };
};

const save = async () => {
  if (!name.value.trim()) return;
  errorMessage.value = '';
  try {
    await tablesStore.createSavedQuery(props.tableId, buildPayload());
    name.value = '';
    description.value = '';
    conditions.value = [{ column: '', operator: 'eq', parameter: '' }];
  } catch (error) {
    errorMessage.value = error?.response?.data?.message || t('TABLES.ERROR');
  }
};

const remove = async savedQuery => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('TABLES.QUERIES.CONFIRM_DELETE', { name: savedQuery.name }))) return;
  await tablesStore.deleteSavedQuery(props.tableId, savedQuery.id);
};

onMounted(() => tablesStore.fetchSavedQueries(props.tableId));
</script>

<template>
  <div class="flex flex-col gap-4">
    <p class="m-0 text-xs text-n-slate-11">{{ t('TABLES.QUERIES.HINT') }}</p>

    <ul class="m-0 list-none">
      <li
        v-for="savedQuery in tablesStore.savedQueries"
        :key="savedQuery.id"
        class="flex items-start justify-between gap-2 py-2 border-t border-n-weak"
      >
        <div>
          <div class="text-sm font-medium text-n-slate-12">
            {{ savedQuery.name }}
            <code class="ml-1 text-xs text-n-slate-11">{{ savedQuery.slug }}</code>
          </div>
          <p v-if="savedQuery.description" class="m-0 text-xs text-n-slate-11">
            {{ savedQuery.description }}
          </p>
          <code class="text-xs text-n-slate-11">
            ({{ savedQuery.parameters.map(p => p.name).join(', ') }})
          </code>
        </div>
        <Button
          icon="i-lucide-trash-2"
          size="xs"
          variant="ghost"
          color="ruby"
          @click="remove(savedQuery)"
        />
      </li>
    </ul>

    <div class="flex flex-col gap-2 pt-4 border-t border-n-weak">
      <div class="flex flex-wrap gap-2">
        <Input v-model="name" :label="t('TABLES.QUERIES.NAME')" class="w-56" />
        <Input v-model="description" :label="t('TABLES.QUERIES.DESCRIPTION')" class="flex-1" />
      </div>

      <div
        v-for="(condition, index) in conditions"
        :key="index"
        class="flex flex-wrap items-end gap-2"
      >
        <label class="flex flex-col gap-1 text-xs text-n-slate-11">
          {{ t('TABLES.QUERIES.COLUMN') }}
          <select
            v-model="condition.column"
            class="px-2 py-1 text-sm border rounded border-n-weak bg-transparent text-n-slate-12"
          >
            <option value="" />
            <option v-for="column in columns" :key="column.key" :value="column.key">
              {{ column.name }}
            </option>
          </select>
        </label>
        <label class="flex flex-col gap-1 text-xs text-n-slate-11">
          {{ t('TABLES.QUERIES.OPERATOR') }}
          <select
            v-model="condition.operator"
            class="px-2 py-1 text-sm border rounded border-n-weak bg-transparent text-n-slate-12"
          >
            <option value="eq">{{ t('TABLES.QUERIES.OPERATORS.eq') }}</option>
            <option value="contains">
              {{ t('TABLES.QUERIES.OPERATORS.contains') }}
            </option>
          </select>
        </label>
        <Input
          v-model="condition.parameter"
          :label="t('TABLES.QUERIES.PARAMETER')"
          class="w-40"
          :placeholder="t('TABLES.QUERIES.PARAMETER_PLACEHOLDER')"
        />
      </div>

      <div class="flex gap-2">
        <Button
          :label="t('TABLES.QUERIES.ADD_CONDITION')"
          size="xs"
          variant="faded"
          @click="addCondition"
        />
        <Button :label="t('TABLES.QUERIES.SAVE')" size="sm" @click="save" />
      </div>

      <p v-if="errorMessage" class="p-2 m-0 text-sm rounded text-n-ruby-11 bg-n-ruby-3">
        {{ errorMessage }}
      </p>
    </div>
  </div>
</template>
